/**
 * <sn-expand-layout>
 * Layout is pure CSS. This element only decides which item is open and
 * which video plays. Input is read from pointer events (pointerType), not from
 * the user agent or the screen width, so hybrid devices (touch laptops, iPad +
 * trackpad) behave correctly and nothing breaks on resize or rotation.
 */
if (!customElements.get('sn-expand-layout')) {
  const ROW_QUERY = '(min-width: 1150px)';
  const HOVER_DELAY = 80;
  const SCROLL_GAP = 12;

  class SnExpandLayout extends HTMLElement {
    #abort = null;
    #observer = null;
    #hoverTimer = 0;
    #anchorTimer = 0;
    #inView = false;
    #rowMq = window.matchMedia(ROW_QUERY);
    #motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    get items() {
      return [...this.querySelectorAll(':scope > .sn-xl-item')];
    }
    get isRow() {
      return this.#rowMq.matches;
    }
    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      if (!this.items.some((item) => item.hasAttribute('data-active'))) {
        this.items[0] && this.#setOpen(this.items[0], true);
      }
      this.dataset.ready = '';

      this.addEventListener('pointerover', this.#onPointerOver, { signal });
      this.addEventListener('pointerleave', this.#cancelHover, { signal });
      this.addEventListener('focusin', this.#onFocusIn, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
      this.#rowMq.addEventListener('change', this.#onModeChange, { signal });
      this.#motionMq.addEventListener('change', this.#syncVideos, { signal });
      document.addEventListener('visibilitychange', this.#syncVideos, { signal });

      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.#inView = entry.isIntersecting;
          this.#syncVideos();
        },
        { rootMargin: '200px 0px' }
      );
      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#observer?.disconnect();
      this.#cancelHover();
      this.items.forEach((item) => item.querySelector('video')?.pause());
    }

    /* ---------- state ---------- */

    open(item) {
      if (!item || item.hasAttribute('data-active')) return;
      // One open item (and one playing video) at a time on every device
      this.items.forEach((other) => other !== item && this.#setOpen(other, false));
      this.#setOpen(item, true);
      this.#syncVideos();
    }

    toggle(item) {
      if (item.hasAttribute('data-active')) {
        this.#setOpen(item, false);
        this.#syncVideos();
      } else {
        this.open(item);
      }
    }

    #setOpen(item, open) {
      item.toggleAttribute('data-active', open);
      item.querySelector('[data-toggle]')?.setAttribute('aria-expanded', String(open));
    }

    /* ---------- input ---------- */

    #itemFrom(target) {
      const item = target.closest?.('.sn-xl-item');
      return item?.parentElement === this ? item : null;
    }

    #onPointerOver = (event) => {
      if (!this.isRow || event.pointerType !== 'mouse') return;
      const item = this.#itemFrom(event.target);
      if (!item || item.hasAttribute('data-active')) return;
      this.#cancelHover();
      // Small intent delay so sweeping the cursor across the row doesn't thrash
      this.#hoverTimer = window.setTimeout(() => this.open(item), HOVER_DELAY);
    };

    #cancelHover = () => {
      window.clearTimeout(this.#hoverTimer);
    };

    #onFocusIn = (event) => {
      // Keyboard focus only; a tap also focuses the link and must not count as "open + follow"
      if (this.isRow && event.target.matches(':focus-visible')) this.open(this.#itemFrom(event.target));
    };

    #onClick = (event) => {
      const item = this.#itemFrom(event.target);
      if (!item) return;

      if (event.target.closest('[data-toggle]')) {
        event.preventDefault();
        const target = item.hasAttribute('data-active') ? null : this.#videoTarget(item);
        this.toggle(item);
        if (target !== null) this.#scrollTo(target);
        return;
      }

      // Row + touch/pen: first tap opens the card, a tap on the open card follows its link
      if (this.isRow && !item.hasAttribute('data-active')) {
        event.preventDefault();
        this.#cancelHover();
        this.open(item);
      }
    };

    /* ---------- scroll anchoring (stacked view) ---------- */

    // Page offset where the video's top will sit once the open/close animation ends,
    // minus the sticky header. Reads the end layout directly: apply it with transitions
    // off, measure, then put the old state back so the real toggle still animates.
    // (Card heights change on tablet as text rewraps, so the offset can't be derived.)
    #videoTarget(item) {
      const video = item.querySelector('.sn-xl-item__video');
      if (!video) return null;
      const wasOpen = this.items.filter((other) => other.hasAttribute('data-active'));

      this.toggleAttribute('data-measuring', true);
      this.items.forEach((other) => other.toggleAttribute('data-active', other === item));
      const top = window.scrollY + video.getBoundingClientRect().top;
      this.items.forEach((other) => other.toggleAttribute('data-active', wasOpen.includes(other)));
      this.getBoundingClientRect(); // flush the restored state while transitions are still off
      this.removeAttribute('data-measuring');

      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return top - header - SCROLL_GAP;
    }

    #scrollTo(top) {
      const root = document.documentElement;
      // Native scroll anchoring would also correct for the collapsing item and double the shift
      root.style.overflowAnchor = 'none';
      window.clearTimeout(this.#anchorTimer);
      this.#anchorTimer = window.setTimeout(() => root.style.removeProperty('overflow-anchor'), 1000);

      window.scrollTo({ top: Math.max(0, top), behavior: this.#motionMq.matches ? 'auto' : 'smooth' });
    }

    #onEditorSelect = (event) => {
      this.open(this.#itemFrom(event.target));
    };

    #onModeChange = () => {
      // The row always needs an open item; the stacked view may have none
      if (this.isRow && !this.items.some((item) => item.hasAttribute('data-active'))) {
        this.items[0] && this.#setOpen(this.items[0], true);
      }
      this.#syncVideos();
    };

    /* ---------- video ---------- */

    #syncVideos = () => {
      const canPlay = this.#inView && !document.hidden && !this.#motionMq.matches;
      const srcKey = this.isRow ? 'srcDk' : 'srcMb';

      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;

        if (canPlay && item.hasAttribute('data-active')) {
          const src = video.dataset[srcKey] || video.dataset.srcDk;
          // Videos load only once their item is opened, and only the size for this layout
          if (src && video.dataset.loaded !== src) {
            video.src = src;
            video.dataset.loaded = src;
          }
          const playing = video.play();
          playing?.catch?.((error) => {
            if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') console.error(error);
          });
        } else if (!video.paused) {
          video.pause();
        }
      });
    };
  }

  customElements.define('sn-expand-layout', SnExpandLayout);
}
